/** Longest a normalized roomId may be. Mirrors the server's `MAX_ROOM_ID_LENGTH`. */
export const MAX_ROOM_ID_LENGTH = 128;

/**
 * Canonicalize a client-chosen roomId (a typed room name, or the path segment of
 * an invite link) into a stable, URL- and KV-safe slug — or `null` when nothing
 * usable survives.
 *
 * This MUST match the server's `normalizeRoomId` (`src/services/roomId.ts` in
 * andor-server): the server re-normalizes and is authoritative, but the client
 * normalizes up front so the id used locally (password-store key, presence lock,
 * SFU room, invite link) agrees with the one the server stores. Rules: NFKD-fold
 * + drop combining marks, lowercase (ids are case-insensitive), whitespace runs →
 * `-`, keep only the RFC-3986 unreserved set `[a-z0-9._~-]`, collapse repeated
 * `-`, trim edge punctuation, cap at {@link MAX_ROOM_ID_LENGTH}. Idempotent and a
 * no-op on server-minted UUIDs. Returns `null` for an input that reduces to "".
 */
export function normalizeRoomId(raw: string): string | null {
  const slug = raw
    .normalize('NFKD')
    .toLowerCase()
    .replace(/\s+/g, '-')
    .replace(/[^a-z0-9._~-]+/g, '')
    .replace(/-{2,}/g, '-')
    .replace(/^[-._~]+|[-._~]+$/g, '')
    .slice(0, MAX_ROOM_ID_LENGTH)
    .replace(/[-._~]+$/g, '');
  return slug.length > 0 ? slug : null;
}
